/**
 * TypeScript view of the `tents` format.
 * Source of truth: `shared/schema/tents.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

/** Tents along one line, or null when the line says nothing. */
export type Count = number | null;

export interface TentsDefinition {
  puzzleType: "tents";
  typeVersion: number;
  rows: number;
  cols: number;
  /** One string per row: `T` a tree, `.` open ground. */
  trees: string[];
  counts: { rows: Count[]; cols: Count[] };
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const TREE = "T";
export const TENT = "A";
export const EMPTY = ".";
/** A solving aid: the player is sure the cell stays grass. Never sent to the server. */
export const GRASS = "x";
export type Mark = typeof TENT | typeof GRASS | typeof EMPTY;

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface TentsProgress {
  version: 1;
  /** One string per row, `A` a tent, `x` grass, `.` undecided. */
  rows: string[];
}

/** One string per row, `A` a tent and `.` anything else: grass is folded into `.`. */
export interface TentsSubmission {
  rows: string[];
}

/** The author's tents, in the submission format. */
export type TentsSolution = TentsSubmission;

export const MAX_SIZE = 30;

export type Axis = "row" | "col";
export type Cell = [row: number, col: number];

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
